import * as React from 'react';
import Chart from 'chart.js';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import usePrevious from '../hooks/usePrevious';
import { lastClickedTimestampAtom, widgetGraphSelectedValuesAtom } from './Graphs/GraphState';

interface IComponentProps {
  data: any;
  options?: any;
  legend?: any;
  type?: string;
  height?: string;
  isSelectable?: boolean;
  isOnline?: boolean;
}

export default function ChartComponent(props: IComponentProps) {
  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const [selectedValues, setSelectedValues] = React.useState<string[] | null>(null);
  const lastClickedTimestamp = useRecoilValue<string>(lastClickedTimestampAtom);

  const getFisrtTimestamp = () => {
    if (props.data && props.data.labels) {
      return props.data.labels[0];
    }
    return '';
  };

  const getLastTimestamp = () => {
    if (props.data && props.data.labels) {
      return props.data.labels[props.data.labels.length - 1];
    }
    return '';
  };

  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | null>();
  const previousFirtTimestamp = usePrevious(getFisrtTimestamp(), { jsonParse: false });
  const previousLastTimestamp = usePrevious(getLastTimestamp(), { jsonParse: false });

  const defaultOptions = {
    maintainAspectRatio: false,
    legend: props.legend || {
      display: false,
    },
    tooltips: {
      enabled: true,
    },
    scales: {
      yAxes: [
        {
          type: 'linear',
          ticks: {
            beginAtZero: true,
            maxTicksLimit: 30,
          },
          display: true,
          gridLines: {
            display: true,
            drawOnChartArea: false,
            drawTicks: true,
            tickMarkLength: 5,
          },
        },
      ],
      xAxes: [
        {
          gridLines: {
            display: true,
            drawOnChartArea: false,
            drawTicks: true,
            tickMarkLength: 5,
          },
          ticks: {
            beginAtZero: true,
            maxTicksLimit: 10,
          },
        },
      ],
    },
    elements: {
      point: {
        radius: 3,
        hitRadius: 10,
        hoverRadius: 10,
      },
    },
    animation: false,
  };

  const renderChart = React.useCallback(() => {
    if (!canvasRef.current) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: props.type || 'line',
      data: props.data || [],
      options: props.options ? { ...props.options } : { ...defaultOptions },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIsDataChanged = () => {
    // if (!props.isOnline) {
    //   return true;
    // }
    return (
      getFisrtTimestamp() !== previousFirtTimestamp ||
      !previousFirtTimestamp ||
      getLastTimestamp() !== previousLastTimestamp ||
      !previousLastTimestamp
    );
  };

  React.useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current && getIsDataChanged()) {
      chartRef.current.data = props.data;
      chartRef.current.options = { ...props.options };
      chartRef.current.update();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.options]);

  React.useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.data = props.data;
      chartRef.current.options = { ...props.options };
      chartRef.current.update();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastClickedTimestamp]);

  const destroyChart = () => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }
  };

  React.useEffect(() => {
    if (!chartRef.current) return;
    destroyChart();
    setTimeout(renderChart);
  }, [props.type, renderChart]);

  React.useEffect(() => {
    renderChart();
    return () => destroyChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (props.isSelectable && selectedValues && selectedValues.length === 2) {
      setWidgetGraphSelectedValues([selectedValues[0], selectedValues[1]]);
    } else if (!props.isSelectable && selectedValues) {
      setSelectedValues(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isSelectable, selectedValues]);

  React.useEffect(() => {
    if (props.isSelectable) {
      if (canvasRef.current && chartRef.current && props.data.labels) {
        const overlay: any = document.getElementById('overlay');

        if (overlay) {
          let startIndex = 0;
          overlay.width = canvasRef.current.width;
          overlay.height = canvasRef.current.height;
          const selectionContext = overlay.getContext('2d');
          const selectionRect = {
            w: 0,
            startX: 0,
            startY: 0,
          };

          let drag = false;

          canvasRef.current.addEventListener('pointerdown', (evt) => {
            if (chartRef.current && canvasRef.current) {
              const points = chartRef.current.getElementsAtEventForMode(evt, 'index', {
                intersect: false,
              });
              startIndex = points[0]._index;
              const rect = canvasRef.current.getBoundingClientRect();
              selectionRect.startX = evt.clientX - rect.left;
              selectionRect.startY = chartRef.current.chartArea.top;
              drag = true;
              // save points[0]._index for filtering
            }
          });

          canvasRef.current.addEventListener('pointermove', (evt) => {
            if (chartRef.current && canvasRef.current) {
              const rect = canvasRef.current.getBoundingClientRect();
              if (drag) {
                const rectPointerMove = canvasRef.current.getBoundingClientRect();
                selectionRect.w = evt.clientX - rectPointerMove.left - selectionRect.startX;
                selectionContext.globalAlpha = 0.5;
                selectionContext.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

                // Set the fill style before drawing the rectangle
                selectionContext.fillStyle = '#1C957A';

                selectionContext.fillRect(
                  selectionRect.startX,
                  selectionRect.startY,
                  selectionRect.w,
                  chartRef.current.chartArea.bottom - chartRef.current.chartArea.top
                );
              } else {
                selectionContext.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                const x = evt.clientX - rect.left;
                if (x > chartRef.current.chartArea.left) {
                  // Set a different fill style for the hover effect
                  selectionContext.fillStyle = '#1C957A';

                  selectionContext.fillRect(
                    x,
                    chartRef.current.chartArea.top,
                    2,
                    chartRef.current.chartArea.bottom - chartRef.current.chartArea.top
                  );
                }
              }
            }
          });

          canvasRef.current.addEventListener('pointerup', (evt) => {
            if (chartRef.current && canvasRef.current) {
              const points = chartRef.current.getElementsAtEventForMode(evt, 'index', {
                intersect: false,
              });
              drag = false;

              if (startIndex !== undefined && points[0]?._index !== undefined) {
                setSelectedValues([String(startIndex), String(points[0]._index)]);
              }
            }
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isSelectable]);

  return (
    <div style={{ height: props.height || '300px' }}>
      {props.isSelectable && (
        <canvas
          id="overlay"
          width="600"
          height="400"
          style={{ position: 'absolute', pointerEvents: 'none' }}
        />
      )}
      <canvas
        ref={canvasRef}
        id="componentChart"
        style={{ cursor: props.isSelectable ? 'grab' : '' }}
      />
    </div>
  );
}
